import React, { useCallback, useMemo, useRef, useState } from 'react'
import dayjs from 'dayjs'
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material'

import Modal from '../../../components/common/modal/Modal'
import { Loader } from '../../../components/common/Loader'
import ButtonComponent from '../../../components/common/button/Button'
import DataTable from '../../../components/common/datatable/DataTable'

const ArchiveModal = ({
    open,
    handleClose,
    title,
    loading,
    archivedData,
    onSelectionChanged,
    rowSelection,
    getRowStyle,
    onRowSelected,
    saveBtnDisabled,
    onUndoArchivedRecords, 
    userRole,
}) => {
    const [columnState, setColumnState] = useState(null);
    const [selectedColumn, setSelectedColumn] = useState("");
    const [disableFilterBtn, setDisableFilterBtn] = useState(true);
    const [filteredTotalColumn, setFilteredTotalColumn] = useState(0);

    const formatTimestamp = (timestamp) => {
    if (!timestamp) return "-";
    return dayjs(timestamp).format("DD-MMM-YYYY HH:mm:ss");
    };

    const renderExpandableText = (params, maxLength = 50) => {
    const text = params.value || ""; // The error details text
    const isLongText = text.length > maxLength;

    // Initialize the showFullText property for the row if not already set
    if (!params.node.showFullText) {
        params.node.showFullText = false;
    }

    const toggleText = () => {
        params.node.showFullText = !params.node.showFullText;
        params.api.refreshCells({ rowNodes: [params.node], force: true }); // Refresh the cell to update the UI
    };

    return isLongText ? (
        <div>
        {params.node.showFullText ? (
            <>
            <span>{text}</span>{" "}
            <button
                onClick={toggleText}
                style={{
                background: "none",
                border: "none",
                color: "#181d1f",
                cursor: "pointer",
                textDecoration: "underline",
                fontWeight: "bold",
                }}
            >
                Less
            </button>
            </>
        ) : (
            <>
            <span>{text.substring(0, maxLength)}...</span>{" "}
            <button
                onClick={toggleText}
                style={{
                background: "none",
                border: "none",
                color: "#181d1f",
                cursor: "pointer",
                textDecoration: "underline",
                fontWeight: "bold",
                }}
            >
                More
            </button>
            </>
        )}
        </div>
    ) : (
        <span>{text}</span>
    );
    };

  const columnData = useMemo(() => {
    if(archivedData?.sisPriceMemo) {
        return [
        {
          headerName: "Is New",
          field: "isNew",
          sortable: true,
          resizable: false,
          filterParams: {
            textFormatter: (value) => {
              if (typeof value === "string") {
                const v = value.trim().toLowerCase();
                if (v === "!") return "!";
                if (v === "-") return "-";
                return v;
              }
              return value;
            },
          },
          valueGetter: (params) => {
            if (params.data?.isNew === true) return "!";
            if (params.data?.isNew === false) return "-";
            return "";
          },
          valueFormatter: (params) => params.value,
          cellRenderer: (params) => {
            if (params.value === "!") {
              return (
                <span
                  style={{
                    fontWeight: "bold",
                    fontSize: "20px",
                    textAlign: "center",
                  }}
                >
                  !
                </span>
              );
            }
            if (params.value === "-") {
              return (
                <span
                  style={{
                    color: "#616161",
                    fontWeight: "bold",
                    fontSize: "16px",
                  }}
                >
                  -
                </span>
              );
            }
            return "";
          },
          unSortIcon: true,
        },
        {
          headerName: "Ingredient",
          field: "ingredient",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          width: 150,
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("ingredient"),
          },
        },
        {
          headerName: "S-Code",
          field: "specequivalentcode",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("specequivalentcode"),
          },
        },
        {
          headerName: "Year",
          field: "yearSIS",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("yearSIS"),
          },
        },
        {
          headerName: "Price Tag ID",
          field: "priceTagId",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("priceTagId"),
          },
        },
        {
          headerName: "Division",
          field: "division",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("division"),
          },
        },
        {
          headerName: "Change Date",
          field: "changeDate",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("changeDate"),
          },
        },
        {
          headerName: "Plant Id",
          field: "plantId",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("plantId"),
          },
        },
        {
          headerName: "Sap Plant Number",
          field: "sapPlantId",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("sapPlantId"),
          },
        },
        {
          headerName: "Plant Name",
          field: "plantName",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("plantName"),
          },
        },
        {
          headerName: "Supplier Id",
          field: "supplierId",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("supplierId"),
          },
        },
        {
          headerName: "Sap Vendor Number",
          field: "sapSupplierId",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("sapSupplierId"),
          },
        },
        {
          headerName: "Sap Vendor Description",
          field: "sapSupplierDescription",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("sapSupplierDescription"),
          },
        },
        {
          headerName: "Supplier",
          field: "supplierName",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("supplierName"),
          },
        },
        {
          headerName: "Location Name",
          field: "locationName",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("locationName"),
          },
        },
        {
          headerName: "Supplier Country",
          field: "facilitycountry",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("facilitycountry"),
          },
        },
        {
          headerName: "PIN",
          field: "pin",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("pin"),
          },
        },
        {
          headerName: "Sap Material Number",
          field: "sapMaterialNumber",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("sapMaterialNumber"),
          },
        },
        {
          headerName: "Sap Material Description",
          field: "sapMaterialDescription",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "paymentTerms-cell",
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("sapMaterialDescription"),
          },
        },
        {
          headerName: "Material Group",
          field: "extmatgrp",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("extmatgrp"),
          },
        },
        {
          headerName: "Package Name",
          field: "packageName",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("packageName"),
          },
        },
        {
          headerName: "Package Size",
          field: "packSize",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("packSize"),
          },
        },
        {
          headerName: "Bulk Flag",
          field: "isbulk",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("isbulk"),
          },
        },
        {
          headerName: "UOM",
          field: "uom",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("uom"),
          },
        },
        {
          headerName: "Price per Kg",
          field: "pricePerKG",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("pricePerKG"),
          },
        },
        {
          headerName: "Currency",
          field: "currency",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("currency"),
          },
        },
        {
          headerName: "Effective From",
          field: "effectiveStartDate",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("effectiveStartDate"),
          },
        },
        {
          headerName: "Effective To",
          field: "effectiveEndDate",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("effectiveEndDate"),
          },
        },
        {
          headerName: "Allocation Quantity",
          field: "allocationQty",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("allocationQty"),
          },
        },
        {
          headerName: "Allocation Notes",
          field: "allocationNotes",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "paymentTerms-cell",
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("allocationNotes"),
          },
        },
        {
          headerName: "From (Qty)",
          field: "fromQty",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("fromQty"),
          },
        },
        {
          headerName: "To (Qty)",
          field: "toQty",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("toQty"),
          },
        },
        {
          headerName: "MOQ Comments",
          field: "moqComments",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "status-cell",
          cellRenderer: (params) => renderExpandableText(params, 50),
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("moqComments"),
          },
        },
        {
          headerName: "Payment Term",
          field: "paymentTerms",
          sortable: true,
          resizable: true,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "paymentTerms-cell",
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("paymentTerms"),
          },
        },
        {
          headerName: "Manufacturing Lead Time",
          field: "manufacturingLeadTime",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("manufacturingLeadTime"),
          },
        },
        {
          headerName: "Auto Co Ship",
          field: "autoCoship",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("autoCoship"),
          },
        },
        {
          headerName: "Inco Terms",
          field: "incoterms",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("incoterms"),
          },
        },
        {
          headerName: "Named Place",
          field: "namedPlace",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("namedPlace"),
          },
        },
        {
          headerName: "Supply Point Number",
          field: "supplypointid",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("supplypointid"),
          },
        },
        {
          headerName: "Supply Point Description",
          field: "supplypointname",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "paymentTerms-cell",
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("supplypointname"),
          },
        },
        {
          headerName: "Manager Initials",
          field: "managerInitials",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("managerInitials"),
          },
        },
        {
          headerName: "Manager",
          field: "managerName",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("managerName"),
          },
        },
        {
          headerName: "PC Initial",
          field: "pcInitial",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("pcInitial"),
          },
        },
        {
          headerName: "SCA",
          field: "sca",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("sca"),
          },
        },
        {
          headerName: "PMU Date",
          field: "pmudate",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("pmudate"),
          },
          valueFormatter: (params) => formatTimestamp(params.value),
        },
        {
          headerName: "Price Per Lb",
          field: "pricePerLb",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("pricePerLb"),
          },
        },
        {
          headerName: "Manufacturing Facility Number",
          field: "facilitynum",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("facilitynum"),
          },
        },
        {
          headerName: "Manufacturing Facility Description",
          field: "facilityname",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "paymentTerms-cell",
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("facilityname"),
          },
        },
        {
          headerName: "Religious Attributes",
          headerClass: "center-header",
          children: [
            {
              headerName: "Halal",
              field: "halal",
              sortable: true,
              resizable: false,
              unSortIcon: true,
              cellClassRules: {
                "bold-cell": (params) =>
                  params.data.changedFieldNames?.includes("halal"),
              },
            },
            {
              headerName: "Kosher PO",
              field: "kosherPO",
              sortable: true,
              resizable: false,
              unSortIcon: true,
              cellClassRules: {
                "bold-cell": (params) =>
                  params.data.changedFieldNames?.includes("kosherPO"),
              },
            },
            {
              headerName: "Kosher Yr",
              field: "kosherYr",
              sortable: true,
              resizable: false,
              unSortIcon: true,
              cellClassRules: {
                "bold-cell": (params) =>
                  params.data.changedFieldNames?.includes("kosherYr"),
              },
            },
            {
              headerName: "Kosher",
              field: "kosherstatus",
              sortable: true,
              resizable: false,
              unSortIcon: true,
              cellClassRules: {
                "bold-cell": (params) =>
                  params.data.changedFieldNames?.includes("kosherstatus"),
              },
            },
            {
              headerName: "Kosher Passover",
              field: "kosherkfpner",
              sortable: true,
              resizable: false,
              unSortIcon: true,
              cellClassRules: {
                "bold-cell": (params) =>
                  params.data.changedFieldNames?.includes("kosherkfpner"),
              },
            },
            {
              headerName: "Kosher Passover (OU)",
              field: "kosherkfpou",
              sortable: true,
              resizable: false,
              unSortIcon: true,
              cellClassRules: {
                "bold-cell": (params) =>
                  params.data.changedFieldNames?.includes("kosherkfpou"),
              },
            },
            {
              headerName: "Kosher Year (NER)",
              field: "kosherkyrner",
              sortable: true,
              resizable: false,
              unSortIcon: true,
              cellClassRules: {
                "bold-cell": (params) =>
                  params.data.changedFieldNames?.includes("kosherkyrner"),
              },
            },
            {
              headerName: "Kosher Year (OU)",
              field: "kosherkyrou",
              sortable: true,
              resizable: false,
              unSortIcon: true,
              cellClassRules: {
                "bold-cell": (params) =>
                  params.data.changedFieldNames?.includes("kosherkyrou"),
              },
            },
            {
              headerName: "Allergen",
              field: "allergenstatus",
              sortable: true,
              resizable: false,
              unSortIcon: true,
              cellClassRules: {
                "bold-cell": (params) =>
                  params.data.changedFieldNames?.includes("allergenstatus"),
              },
            },
            {
              headerName: "Allergen Description",
              field: "allergenknown",
              sortable: true,
              resizable: false,
              unSortIcon: true,
              cellClassRules: {
                "bold-cell": (params) =>
                  params.data.changedFieldNames?.includes("allergenknown"),
              },
            },
            {
              headerName: "Fairtrade",
              field: "fairtradecertified",
              sortable: true,
              resizable: false,
              unSortIcon: true,
              cellClassRules: {
                "bold-cell": (params) =>
                  params.data.changedFieldNames?.includes("fairtradecertified"),
              },
            },
          ],
        },
        {
          headerName: "Forecast",
          field: "foreCastFile",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("foreCastFile"),
          },
        },
        {
          headerName: "Remarks",
          field: "mappingRemarks",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          // cellClass: "paymentTerms-cell",
          cellRenderer: (params) => renderExpandableText(params, 50),
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("mappingRemarks"),
          },
        },
        {
          headerName: "Status",
          pinned: "right",
          field: "status",
          sortable: true,
          resizable: true,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "status-cell",
          cellClassRules: {
            "bold-cell": (params) =>
              params.data.changedFieldNames?.includes("status"),
          },
        },
      ];
    } else if(archivedData?.agriPriceMemo) {
      return [
        {
          headerName: 'Is New',
          field: "isNew",
          sortable: true,
          resizable: false,
          filterParams: {
            textFormatter: (value) => {
              if (typeof value === "string") {
                const v = value.trim().toLowerCase();
                if (v === "!") return "!";
                if (v === "-") return "-";
                return v;
              }
              return value;
            },
          },
          valueGetter: (params) => {
            if (params.data?.isNew === true) return "!";
            if (params.data?.isNew === false) return "-";
            return "";
          },
          valueFormatter: (params) => params.value,
          cellRenderer: (params) => {
            if (params.value === "!") {
              return (
                <span
                  style={{
                    fontWeight: "bold",
                    fontSize: "20px",
                    textAlign: "center",
                  }}
                >
                  !
                </span>
              );
            }
            if (params.value === "-") {
              return (
                <span
                  style={{
                    color: "#616161",
                    fontWeight: "bold",
                    fontSize: "16px",
                  }}
                >
                  -
                </span>
              );
            }
            return "";
          },
          pinned: "left",
          unSortIcon: true,
          width: 130,
        },
        {
          headerName: 'Plant Id',
          field: "plantId",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('plantId')
          }
        },
        {
          headerName: 'Vendor Number',
          field: "vendorNumber",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('vendorNumber')
          }
        },
        {
          headerName: 'Material Number',
          field: "materialNumber",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('materialNumber')
          }
        },
        {
          headerName: 'Plant Name',
          field: "plantName",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('plantName')
          }
        },
        {
          headerName: 'Supplier',
          field: "supplierName",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('supplierName')
          }
        },
        {
          headerName: 'Material Description',
          field: "materialDescription",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('materialDescription')
          }
        },
        {
          headerName: 'S No.',
          field: "snumber",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('snumber')
          }
        },
        {
          headerName: 'Currency',
          field: "currency",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('currency')
          }
        },
        {
          headerName: 'Volume',
          field: "volume",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('volume')
          }
        },
        {
          headerName: 'Group',
          field: "groupAgri",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('groupAgri')
          }
        },
        {
          headerName: 'Sub Category',
          field: "subCategory",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('subCategory')
          }
        },
        {
          headerName: 'Brand',
          field: "brand",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('brand')
          }
        },
        {
          headerName: 'Prorement Manager',
          field: "prorementManager",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('prorementManager')
          }
        },
        {
          headerName: 'UOM',
          field: "uom",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('uom')
          }
        },
        {
          headerName: 'Geography',
          field: "geography",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('geography')
          }
        },
        {
          headerName: 'Picasso Approval',
          field: "picassoApproval",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('picassoApproval')
          }
        },
        {
          headerName: 'Material Origin',
          field: "materialOrigin",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('materialOrigin')
          }
        },
        {
          headerName: 'LeadTime',
          field: "leadTime",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('leadTime')
          }
        },
        {
          headerName: 'Ship From Country',
          field: "shipFromCountry",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('shipFromCountry')
          }
        },
        {
          headerName: 'Ship From City',
          field: "shipFromCity",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('shipFromCity')
          }
        },
        {
          headerName: 'Payment Term',
          field: "paymentTerm",
          sortable: true,
          resizable: true,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "paymentTerms-cell",
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('paymentTerm')
          }
        },
        {
          headerName: 'Material Cost Per UOM',
          field: "materialCostPerUOM",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('materialCostPerUOM')
          }
        },
        {
          headerName: 'Total Cost Per UOM',
          field: "totalCostPerUOM",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('totalCostPerUOM')
          }
        },
        {
          headerName: 'MOQ Comments',
          field: "moqComments",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "paymentTerms-cell status-cell",
          cellRenderer: (params) => renderExpandableText(params, 50),
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('moqComments')
          }
        },
        {
          headerName: 'RFA Ft Per UOM',
          field: "rfaFtPerUOM",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('rfaFtPerUOM')
          }
        },
        {
          headerName: 'Bid Incoterm',
          field: "bidIncoterm",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('bidIncoterm')
          }
        },
        // {
        //   headerName: 'Open PO',
        //   field: "openPO",
        //   sortable: true,
        //   resizable: false,
        //   filterParams: commonFilterParams,
        //   filter: commonFilter,
        //   suppressMovable: true,
        //   unSortIcon: true,
        //   cellClassRules: {
        //     'bold-cell': (params) => params.data.changedFieldNames?.includes('openPO')
        //   }
        // },
        {
          headerName: 'Bid Incoterm Location',
          field: "bidIncotermLocation",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('bidIncotermLocation')
          }
        },
        {
          headerName: 'Price Effective Date',
          field: "priceEffectiveDate",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('priceEffectiveDate')
          }
        },
        {
          headerName: 'Price EndDate',
          field: "priceEndDate",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('priceEndDate')
          }
        },
        {
          headerName: 'Packaging Size',
          field: "packagingSize",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('packagingSize')
          }
        },
        {
          headerName: 'Forecast',
          field: "foreCastFile",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('foreCastFile')
          }
        },
        {
          headerName: 'Status',
          pinned: "right",
          field: "status",
          sortable: true,
          resizable: true,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "status-cell",
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('status')
          }
        },
      ]
    } else if (archivedData?.packagingPriceMemo) {
      return [
        {
          headerName: 'Is New',
          field: "isNew",
          sortable: true,
          resizable: false,
          filterParams: {
            textFormatter: (value) => {
              if (typeof value === "string") {
                const v = value.trim().toLowerCase();
                if (v === "!") return "!";
                if (v === "-") return "-";
                return v;
              }
              return value;
            },
          },
          valueGetter: (params) => {
            if (params.data?.isNew === true) return "!";
            if (params.data?.isNew === false) return "-";
            return "";
          },
          valueFormatter: (params) => params.value,
          cellRenderer: (params) => {
            if (params.value === "!") {
              return (
                <span
                  style={{
                    fontWeight: "bold",
                    fontSize: "20px",
                    textAlign: "center",
                  }}
                >
                  !
                </span>
              );
            }
            if (params.value === "-") {
              return (
                <span
                  style={{
                    color: "#616161",
                    fontWeight: "bold",
                    fontSize: "16px",
                  }}
                >
                  -
                </span>
              );
            }
            return "";
          },
          pinned: "left",
          unSortIcon: true,
          width: 130,
        },
        {
          headerName: 'Plant Id',
          field: "plantId",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('plantId')
          }
        },
        {
          headerName: 'Vendor Number',
          field: "vendorNumber",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('vendorNumber')
          }
        },
        {
          headerName: 'Supplier Item No',
          field: "supplierItemNo",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('supplierItemNo')
          }
        },
        {
          headerName: 'CC Material No',
          field: "ccMaterialNo",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('ccMaterialNo')
          }
        },
        {
          headerName: 'Plant Name',
          field: "plantName",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('plantName')
          }
        },
        {
          headerName: 'Supplier',
          field: "supplierName",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('supplierName')
          }
        },
        {
          headerName: 'Material Description',
          field: "materialDescription",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "status-cell",
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('materialDescription')
          }
        },
        {
          headerName: 'Year',
          field: "yearPckg",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('yearPckg')
          }
        },
        {
          headerName: 'Division',
          field: "division",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('division')
          }
        },
        {
          headerName: 'Location Name',
          field: "locationName",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('locationName')
          }
        },
        {
          headerName: 'Manager',
          field: "managerName",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('managerName')
          }
        },
        {
          headerName: 'Last Change',
          field: "lastChange",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('lastChange')
          }
        },
        {
          headerName: 'Payment Description',
          field: "paymentDescription",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "status-cell",
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('paymentDescription')
          }
        },
        {
          headerName: 'Price',
          field: "price",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('price')
          }
        },
        {
          headerName: 'Supplier Description',
          field: "supplierDescription",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "paymentTerms-cell",
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('supplierDescription')
          }
        },
        {
          headerName: 'Material Type',
          field: "materialType",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('materialType')
          }
        },
        {
          headerName: 'Currency',
          field: "currency",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('currency')
          }
        },
        {
          headerName: 'UOM',
          field: "uom",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('uom')
          }
        },
        {
          headerName: 'Inco Terms',
          field: "incoterms",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('incoterms')
          }
        },
        {
          headerName: 'Named Place',
          field: "namedPlace",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('namedPlace')
          }
        },
        {
          headerName: 'From (Qty)',
          field: "fromQty",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('fromQty')
          }
        },
        {
          headerName: 'To (Qty)',
          field: "toQty",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('toQty')
          }
        },
        {
          headerName: 'Effective From',
          field: "effectiveStartDate",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('effectiveStartDate')
          }
        },
        {
          headerName: 'Effective To',
          field: "effectiveEndDate",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('effectiveEndDate')
          }
        },
        {
          headerName: 'Sap Payment Term',
          field: "sapPaymentTerm",
          sortable: true,
          resizable: true,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "paymentTerms-cell",
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('sapPaymentTerm')
          }
        },
        {
          headerName: 'Manufacturing Lead Time',
          field: "manufacturingLeadTime",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('manufacturingLeadTime')
          }
        },
        {
          headerName: 'MOQ Comments',
          field: "moqComments",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "paymentTerms-cell status-cell",
          cellRenderer: (params) => renderExpandableText(params, 50),
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('moqComments')
          }
        },
        // {
        //   headerName: 'Open PO',
        //   field: "openPO",
        //   sortable: true,
        //   resizable: false,
        //   filterParams: commonFilterParams,
        //   filter: commonFilter,
        //   suppressMovable: true,
        //   unSortIcon: true,
        //   cellClassRules: {
        //     'bold-cell': (params) => params.data.changedFieldNames?.includes('openPO')
        //   }
        // },
        {
          headerName: 'Allocation Quantity',
          field: "allocationQty",
          sortable: true,
          resizable: true,
          unSortIcon: true,
          cellClass: "status-cell",
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('allocationQty')
          }
        },
        {
          headerName: 'Allocation Notes',
          field: "allocationNotes",
          sortable: true,
          resizable: true,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "status-cell",
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('allocationNotes')
          }
        },
        {
          headerName: 'Forecast',
          field: "foreCastFile",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('foreCastFile')
          }
        },
        {
          headerName: 'Status',
          pinned: "right",
          field: "status",
          sortable: true,
          resizable: true,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "status-cell",
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('status')
          }
        },
      ]
    } else if (archivedData?.cepgPriceMemo) {
      return [
        {
          headerName: 'Is New',
          field: "isNew",
          sortable: true,
          resizable: false,
          filterParams: {
            textFormatter: (value) => {
              if (typeof value === "string") {
                const v = value.trim().toLowerCase();
                if (v === "!") return "!";
                if (v === "-") return "-";
                return v;
              }
              return value;
            },
          },
          valueGetter: (params) => {
            if (params.data?.isNew === true) return "!";
            if (params.data?.isNew === false) return "-";
            return "";
          },
          valueFormatter: (params) => params.value,
          cellRenderer: (params) => {
            if (params.value === "!") {
              return (
                <span
                  style={{
                    fontWeight: "bold",
                    fontSize: "20px",
                    textAlign: "center",
                  }}
                >
                  !
                </span>
              );
            }
            if (params.value === "-") {
              return (
                <span
                  style={{
                    color: "#616161",
                    fontWeight: "bold",
                    fontSize: "16px",
                  }}
                >
                  -
                </span>
              );
            }
            return "";
          },
          pinned: "left",
          unSortIcon: true,
          width: 130,
        },
        {
          headerName: 'Plant Id',
          field: "plant",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'Plant Name',
          field: "plantName",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'Material Number',
          field: "material",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "status-cell",
          cellRenderer: (params) => renderExpandableText(params, 50)
        },
        {
          headerName: 'Package Size',
          field: "packSize",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'S-Code',
          field: "scode",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'Year',
          field: "year",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'Division',
          field: "division",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'Forecast File',
          field: "foreCastFile",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'Manager',
          field: "manager",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'Supplier Name',
          field: "supplierName",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "status-cell",
          cellRenderer: (params) => renderExpandableText(params, 50)
        },
        {
          headerName: 'Supplier Number',
          field: "supplierNumber",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'Supplier Description',
          field: "description",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "status-cell",
          cellRenderer: (params) => renderExpandableText(params, 50)
        },
        {
          headerName: 'Gmp Lead',
          field: "gmpLead",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'Change Date',
          field: "changeDate",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'Inco Terms',
          field: "incoterms",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'Named Place',
          field: "namedPlace",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'Price',
          field: "price",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'Material Type',
          field: "material",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'Currency',
          field: "currency",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'UOM',
          field: "uom",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'From (Qty)',
          field: "fromQty",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'To (Qty)',
          field: "toQty",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'Effective From',
          field: "effectiveFrom",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'Effective To',
          field: "effectiveTo",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'PIN',
          field: "pin",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'Alocation Quantity',
          field: "allocationQtyKg",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'Allocation Notes',
          field: "allocationNotes",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'Sap Payment Term',
          field: "paymentTerm",
          sortable: true,
          resizable: true,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "paymentTerms-cell",
        },
        {
          headerName: 'Picasso Facility',
          field: "picassoFacility",
          sortable: true,
          resizable: true,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "paymentTerms-cell",
        },
        {
          headerName: 'Manufacturing Lead Time',
          field: "manufacturingLeadTime",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'MOQ Comments',
          field: "moqComments",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "status-cell",
          cellRenderer: (params) => renderExpandableText(params, 50)
        },
        {
          headerName: 'Error Details',
          field: "errorDetails",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "status-cell",
          cellRenderer: (params) => renderExpandableText(params, 50)
        },
        {
          headerName: 'Open PO',
          field: "openPO",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'Status',
          field: "status",
          sortable: true,
          resizable: true,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "status-cell",
        },
      ]
    }
    }, [archivedData]);

    const pagination = true;
    const paginationPageSize = 30;
    const paginationPageSizeSelector = [30, 50, 100, 500, 2000];

    const selectionColumnDef = useMemo(
    () => ({
        pinned: "left",
    }),
    []
    );

    const gridApiRef = useRef(null); // Reference to the grid API

    const onColumnChange = (e) => {
    const col = e.target.value;
    setSelectedColumn(col);
    if (col && gridApiRef.current) {
        gridApiRef.current.ensureColumnVisible(col);
    }
    };

    const onGridReady = (params) => {
    gridApiRef.current = params.api; // Store the grid API instance

    const savedFilterModel = localStorage.getItem("archivedFilterModel");
    if (savedFilterModel) {
      params.api.setFilterModel(JSON.parse(savedFilterModel));
      setDisableFilterBtn(true);
    }
    // Optionally, force filter to apply if data is already loaded
    params.api.onFilterChanged();

    if (columnState) {
      params.columnApi.applyColumnState({
        state: columnState,
        applyOrder: true,
      });
    }
  };

  const clearAllFilters = () => {
    if (gridApiRef.current) {
    gridApiRef.current.setFilterModel(null); // Clear all filters
    localStorage.removeItem("archivedFilterModel");
    setDisableFilterBtn(true);
    }
};

const onFilterChanged = (params) => {
    if (gridApiRef.current) {
    const filterModel = gridApiRef.current.getFilterModel();
    setDisableFilterBtn(Object.keys(filterModel).length === 0); // Enable button if filters exist
    setFilteredTotalColumn(Object.keys(filterModel).length);
    localStorage.setItem(
        "archivedFilterModel",
        JSON.stringify(filterModel)
    );
    }
};

const onColumnMoved = useCallback((params) => {
    if (
    params &&
    params.columnApi &&
    typeof params.columnApi.getColumnState === "function"
    ) {
    setColumnState(params.columnApi.getColumnState());
    }
}, []);

const gridOptions = {
    getRowId: (params) => {
      return `${params?.data?.runOutputSeqId}`;
    },
    overlayLoadingTemplate: `<span class="ag--overlay-loading-center" >Please wait while data is loading</span>`,
    overlayNoRowsTemplate: `<span class="ag--overlay-no-rows-center" >No Data Available</span>`,
    defaultColDef: {
      sortable: true,
      filter: "agMultiColumnFilter",
      autoHeight: true,
      cellStyle: {
        whiteSpace: "normal",
        wordBreak: "break-word",
        lineHeight: 1.5,
        display: "flex",
        alignItems: "center",
      },
      valueGetter: (params) => {
          if (!params.data) return "";

          const field = params.colDef.field;
          const value = params.data[field];

          return value === null || value === undefined || value === "" ? "" : value;
      },
    },
    onColumnMoved,
    onFilterChanged,
  };

  const getContextMenuItems = (params) => {
    const defaultItems = params.defaultItems || [];

    // Remove the 'export' option
    return defaultItems.filter(item => item !== 'export');
  };

  return (
    <Modal
        open={open}
        handleClose={handleClose}
        title={title}
        fullWidth={true}
        maxWidth="xl"
        fullScreen={true}
        content={
        loading ? (
            <Loader />
        ) : (
            <>
            <div
                style={{
                display: "flex",
                marginBottom: "20px",
                justifyContent: "space-between",
                alignItems: "center",
                }}
            >
                <FormControl sx={{ m: 1, minWidth: 200 }} size="small">
                <InputLabel>Jump to Column</InputLabel>
                <Select
                    id="col-select"
                    value={selectedColumn}
                    label="Jump to Column"
                    onChange={onColumnChange}
                >
                    <MenuItem value="">Select Column</MenuItem>
                    {columnData?.map((col) => (
                    <MenuItem key={col?.field} value={col?.field}>
                        {col?.headerName || col?.field}
                    </MenuItem>
                    ))}
                </Select>
                </FormControl>
                <ButtonComponent
                maxWidth="150px"
                label="Clear All Filters"
                bgColor="black"
                color="white"
                disabled={disableFilterBtn}
                onClick={clearAllFilters}
                />
            </div>
            <DataTable
                columns={columnData}
                rows={archivedData?.sisPriceMemo || archivedData?.agriPriceMemo || archivedData?.packagingPriceMemo || archivedData?.cepgPriceMemo || []}
                paginationProp={pagination}
                paginationPageSize={paginationPageSize}
                paginationPageSizeSelector={paginationPageSizeSelector}
                onRowSelected={onRowSelected}
                selectionColumnDef={selectionColumnDef}
                rowSelection={rowSelection}
                onSelectionChanged={onSelectionChanged}
                onGridReady={onGridReady}
                gridOptions={gridOptions}
                getRowStyle={getRowStyle}
                filteredTotalColumn={filteredTotalColumn}
                getContextMenuItems={getContextMenuItems}
            />
            </>
        )
        }
        action={
        userRole !== "Viewer" &&
        <>
            <ButtonComponent
                maxWidth="170px"
                label="Cancel"
                bgColor="white"
                color="black"
                onClick={handleClose}
            />
            <ButtonComponent
                maxWidth="170px"
                label="Undo"
                bgColor="black"
                color="white"
                disabled={saveBtnDisabled}
                onClick={onUndoArchivedRecords}
            />
        </>
        }
    />
  )
}

export default ArchiveModal